"use client";

import React from "react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
const TukeraLoader = dynamic(() => import("./TukeraLoader"), {
  ssr: false,
});

interface PageLoaderProps {
  page?: "chat" | "ai-assistant" | "dashboard" | "courses" | "default";
  className?: string;
}

/**
 * 页面级加载组件 - 根据页面类型自动选择合适的加载器变体
 */
export default function PageLoader({
  page = "default",
  className,
}: PageLoaderProps) {
  const t = useTranslations("pageLoader");

  // 根据页面类型自动选择加载器配置
  const getLoaderConfig = () => {
    switch (page) {
      case "chat":
        return {
          variant: "voice" as const,
          size: "lg" as const,
          text: t("chat.text"),
          description: t("chat.description"),
          featureLabels: [
            t("chat.features.voiceReady"),
            t("chat.features.assistantOnline"),
          ],
        };

      case "ai-assistant":
        return {
          variant: "ai" as const,
          size: "lg" as const,
          text: t("aiAssistant.text"),
          description: t("aiAssistant.description"),
          featureLabels: [
            t("aiAssistant.features.smartAnalysis"),
            t("aiAssistant.features.personalizedRecommendation"),
          ],
        };

      case "dashboard":
        return {
          variant: "default" as const,
          size: "lg" as const,
          text: t("dashboard.text"),
          description: t("dashboard.description"),
        };

      case "courses":
        return {
          variant: "default" as const,
          size: "lg" as const,
          text: t("courses.text"),
          description: t("courses.description"),
        };

      default:
        return {
          variant: "minimal" as const,
          size: "md" as const,
          text: t("default.text"),
          description: t("default.description"),
        };
    }
  };

  const config = getLoaderConfig();

  return (
    <div
      className={`flex items-center justify-center min-h-screen bg-gradient-to-br from-background via-background to-muted/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 ${className || ""}`}
    >
      <div className="text-center space-y-6 animate-fade-in">
        <TukeraLoader
          variant={config.variant}
          size={config.size}
          text={config.text}
          featureLabels={config.featureLabels}
        />

        {config.description && (
          <p className="text-sm text-muted-foreground/80 dark:text-gray-400 max-w-md mx-auto">
            {config.description}
          </p>
        )}

        {/* 页面特色提示 */}
        {page === "chat" && (
          <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground/60 dark:text-gray-500">
            <span className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              <span>{t("chat.features.voiceReady")}</span>
            </span>
            <span className="flex items-center space-x-1">
              <div
                className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
                style={{ animationDelay: "0.3s" }}
              />
              <span>{t("chat.features.assistantOnline")}</span>
            </span>
          </div>
        )}

        {page === "ai-assistant" && (
          <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground/60 dark:text-gray-500">
            <span className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
              <span>{t("aiAssistant.features.smartAnalysis")}</span>
            </span>
            <span className="flex items-center space-x-1">
              <div
                className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse"
                style={{ animationDelay: "0.3s" }}
              />
              <span>
                {t("aiAssistant.features.personalizedRecommendation")}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
